import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  CircularProgress,
  Button as MuiButton,
  Popover,
} from '@mui/material';
import {
  OpenInFull,
  CloseFullscreen,
  FaceRetouchingNatural as CopilotIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  CleaningServices,
  History as HistoryIcon,
} from '@mui/icons-material';
import DashboardNavigation from '@/components/DashboardNavigation';
import AIAnalyst from '@/components/AIAnalyst';
import ConversationHistoryPanel from '@/components/ConversationHistoryPanel';
import { clientService } from '@/services/api/clientService';
import { snowflakeService } from '@/services/snowflakeService';
import { dashboardService } from '@/services/dashboardService';
import { ConversationHistoryItem } from '@/types';
import { useAuth } from '@/context/AuthContext';

// Question interface (shared with AIAnalyst)
interface Question {
  id?: string;
  title?: string;
  description?: string;
  category: string;
  question?: string;
  icon: React.ReactNode | string;
  prompt?: string;
  color?: string;
  conversation_id?: string;
  status?: 'pending' | 'completed' | 'failed';
  message_id?: string;
  profile_id?: string;
  error?: string;
}

const ExploreStep: React.FC = () => {
  const { workflowId, clientId } = useParams<{
    workflowId: string;
    clientId: string;
  }>();
  const { user } = useAuth();
  const currentUserEmail = user?.email ?? null;

  // Parse numeric IDs for database operations
  const numericClientId =
    clientId && /^\d+$/.test(clientId) ? parseInt(clientId, 10) : undefined;

  const [isMaximized, setIsMaximized] = useState(false);
  const [clientData, setClientData] = useState<any>(null);
  const [activeTab, setActiveTab] = useState(0);

  // Dashboard state
  const [selectedDashboard, setSelectedDashboard] = useState<any>(null);

  // AI Analyst questions state
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionsError, setQuestionsError] = useState<string>('');

  // Copilot state
  const [isCopilotOpen, setIsCopilotOpen] = useState(false);
  const [copilotUrl, setCopilotUrl] = useState<string | null>(null);
  const [isLoadingCopilot, setIsLoadingCopilot] = useState(false);
  const [copilotError, setCopilotError] = useState<string | null>(null);
  const [copilotIframeKey, setCopilotIframeKey] = useState(0);
  const [updatedDashboardConversationId, setUpdatedDashboardConversationId] = useState<string | undefined>(undefined);
  const [dashboardWidth, setDashboardWidth] = useState(60); // Default 60% for dashboard
  const [isDashboardHidden, setIsDashboardHidden] = useState(false);
  const [createdConversations, setCreatedConversations] = useState<Set<string>>(new Set());

  // Free Chat state
  const [freeChatUrl, setFreeChatUrl] = useState<string | null>(null);
  const [isLoadingFreeChat, setIsLoadingFreeChat] = useState(false);
  const [freeChatError, setFreeChatError] = useState<string | null>(null);
  const [freeChatIframeKey, setFreeChatIframeKey] = useState(0);

  // Chat history state
  const [freeChatHistory, setFreeChatHistory] = useState<ConversationHistoryItem[]>([]);
  const [freeChatHistoryLoading, setFreeChatHistoryLoading] = useState(false);
  const [copilotHistory, setCopilotHistory] = useState<ConversationHistoryItem[]>([]);
  const [copilotHistoryLoading, setCopilotHistoryLoading] = useState(false);
  // Per-question history cache for AI Analyst (keyed by question_id)
  const [aiAnalystQuestionHistory, setAiAnalystQuestionHistory] = useState<Record<string, ConversationHistoryItem[]>>({});
  const [aiAnalystQuestionHistoryLoading, setAiAnalystQuestionHistoryLoading] = useState(false);
  // AI Analyst external conversation override (used to load a history item from the toolbar popover)
  const [aiAnalystConversationOverride, setAiAnalystConversationOverride] = useState<{ questionId: string; conversationId: string } | null>(null);
  // Unified history popover
  const [historyPopoverAnchor, setHistoryPopoverAnchor] = useState<HTMLElement | null>(null);
  const [historyPopoverContext, setHistoryPopoverContext] = useState<'freechat' | 'copilot' | 'analyst' | null>(null);

  // Resizer state
  const [isResizing, setIsResizing] = useState(false);
  const [dragIndicatorX, setDragIndicatorX] = useState<number | null>(null);

  // AI Analyst view state
  const [aiAnalystShowingDashboard, setAiAnalystShowingDashboard] = useState(true);
  const [aiAnalystSelectedQuestionId, setAiAnalystSelectedQuestionId] = useState<string | undefined>();

  // Handle dashboard change - close Copilot when switching dashboards
  const handleSelectedDashboardChange = (dashboard: any) => {
    if (selectedDashboard && dashboard && selectedDashboard.Id !== dashboard.Id) {
      setIsCopilotOpen(false);
      setCopilotUrl(null);
      setIsDashboardHidden(false);
      setDashboardWidth(60);
      setUpdatedDashboardConversationId(undefined); // Reset conversation ID when switching dashboards
    }
    setSelectedDashboard(dashboard);
  };

  // Handler for maximize/minimize toggle
  const handleMaximizeToggle = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsMaximized(!isMaximized);
  };

  // Handler for AI Analyst view state changes
  const handleAIAnalystViewStateChange = (isShowingDashboard: boolean, selectedQuestionId?: string) => {
    setAiAnalystShowingDashboard(isShowingDashboard);
    setAiAnalystSelectedQuestionId(selectedQuestionId);
  };

  // Helper method to build Guardrail iframe URL with common parameters
  const buildGuardrailChatUrl = (
    baseUrl: string,
    conversationId: string,
    token: string,
    profileId?: string,
    resolvedClientData?: any
  ): string => {
    const cd = resolvedClientData ?? clientData;
    let url = `${baseUrl}/embed/chat/${conversationId}?theme=light&token=${token}`;

    if (profileId) {
      url += `&profile_id=${encodeURIComponent(profileId)}`;
    }

    if (clientId) {
      url += `&client_id=${encodeURIComponent(clientId)}`;
    }

    if (cd?.client_code) {
      url += `&client_code=${encodeURIComponent(cd.client_code)}`;
    }

    if (cd?.domains) {
      url += `&client_domains=${encodeURIComponent(cd.domains)}`;
    }

    if (cd?.name) {
      url += `&client_name=${encodeURIComponent(cd.name)}`;
    }

    if (workflowId) {
      url += `&workflow_id=${encodeURIComponent(workflowId)}`;
    }

    return url;
  };

  // Handler for refreshing AI Analyst
  const handleRefreshAIAnalyst = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (aiAnalystShowingDashboard) {
      // Refresh all questions when showing dashboard
      handleRefreshAllQuestions();
    } else if (aiAnalystSelectedQuestionId) {
      // Refresh current question when viewing a specific question
      handleRefreshQuestion(aiAnalystSelectedQuestionId);
    }
  };

  // Handler for refreshing Free Chat iframe with new conversation
  const handleRefreshFreeChat = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!workflowId) {
      console.error('No workflow ID available for refresh');
      return;
    }

    try {
      setIsLoadingFreeChat(true);
      setFreeChatError(null);

      // Generate a new conversation ID
      const newConversationId = crypto.randomUUID();
      console.log('Creating new Free Chat conversation:', newConversationId);

      // Save the new conversation to the backend (replaces the old one)
      const saveResponse = await snowflakeService.saveWorkflowConversation(
        workflowId,
        -1,
        newConversationId
      );

      if (!saveResponse.success) {
        throw new Error(saveResponse.error || 'Failed to save new conversation');
      }

      // Update the questions state to reflect the new conversation ID
      setQuestions((prevQuestions) =>
        prevQuestions.map((q) =>
          String(q.id) === '-1'
            ? {
                ...q,
                conversation_id: newConversationId,
                status: 'completed' as const,
                error: undefined,
              }
            : q
        )
      );

      // Create the conversation in Guardrail using the user's authguard token
      try {
        await snowflakeService.createGuardrailConversationWithUserToken(newConversationId);
        console.log('New Free Chat conversation created in Guardrail successfully');
        setCreatedConversations((prev) => new Set(prev).add(newConversationId));
      } catch (conversationError) {
        console.warn('Failed to create Free Chat conversation in Guardrail:', conversationError);
        setCreatedConversations((prev) => new Set(prev).add(newConversationId));
      }

      // Get config and client data, then build new URL
      const [chatConfig, resolvedClientData] = await Promise.all([
        snowflakeService.getGuardrailChatConfig(),
        resolveClientData(),
      ]);
      const url = buildGuardrailChatUrl(
        chatConfig.base_url,
        newConversationId,
        chatConfig.token,
        chatConfig.profile_id,
        resolvedClientData
      );

      // Update the URL and force iframe reload
      setFreeChatUrl(url);
      setFreeChatIframeKey((prevKey) => prevKey + 1);

      console.log('Free Chat refreshed with new conversation');
    } catch (error: any) {
      console.error('Error refreshing Free Chat:', error);
      setFreeChatError('Failed to refresh Free Chat. Please try again.');
    } finally {
      setIsLoadingFreeChat(false);
    }
  };

  // Handler for tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Load free chat when switching to Free Chat tab
    if (newValue === 1) {
      loadFreeChat();
    }
  };

  // Handler for Copilot toggle
  const handleCopilotToggle = async () => {
    if (!isCopilotOpen) {
      setIsCopilotOpen(true); // Open panel immediately
      await loadCopilot();
    } else {
      setIsCopilotOpen(false);
      setIsDashboardHidden(false);
    }
  };

  // Resolve clientData — returns cached value or fetches it if not yet loaded
  const resolveClientData = async (): Promise<any> => {
    if (clientData) return clientData;
    if (!numericClientId) return null;
    try {
      const client = await clientService.getClient(numericClientId);
      setClientData(client);
      return client;
    } catch {
      return null;
    }
  };

  const loadFreeChatHistory = async () => {
    if (!workflowId) return;
    setFreeChatHistoryLoading(true);
    try {
      const history = await snowflakeService.getConversationHistory(workflowId, -1);
      setFreeChatHistory(history);
    } finally {
      setFreeChatHistoryLoading(false);
    }
  };

  const loadCopilotHistory = async (appId: string) => {
    if (!workflowId) return;
    setCopilotHistoryLoading(true);
    try {
      const history = await dashboardService.getCopilotConversationHistory(
        parseInt(workflowId),
        appId
      );
      setCopilotHistory(history);
    } finally {
      setCopilotHistoryLoading(false);
    }
  };

  const handleSelectFreeChatHistory = async (conversationId: string) => {
    if (!workflowId) return;
    try {
      const [chatConfig, resolvedClientData] = await Promise.all([
        snowflakeService.getGuardrailChatConfig(),
        resolveClientData(),
      ]);
      const url = buildGuardrailChatUrl(
        chatConfig.base_url,
        conversationId,
        chatConfig.token,
        chatConfig.profile_id,
        resolvedClientData
      );
      setFreeChatUrl(url);
      setFreeChatIframeKey((k) => k + 1);
    } catch (err) {
      console.error('Failed to load selected Free Chat conversation:', err);
    }
  };

  const handleSelectCopilotHistory = async (conversationId: string) => {
    try {
      const [sessionData, resolvedClientData] = await Promise.all([
        snowflakeService.createGuardrailCopilotSession(),
        resolveClientData(),
      ]);
      const url = buildGuardrailChatUrl(
        sessionData.base_url,
        conversationId,
        sessionData.token,
        sessionData.profile_id,
        resolvedClientData
      );
      setCopilotUrl(url);
      setCopilotIframeKey((k) => k + 1);
      setHistoryPopoverAnchor(null);
    } catch (err) {
      console.error('Failed to load selected Copilot conversation:', err);
    }
  };

  const loadAIAnalystQuestionHistory = async (questionId: string) => {
    if (!workflowId) return;
    setAiAnalystQuestionHistoryLoading(true);
    try {
      const history = await snowflakeService.getConversationHistory(workflowId, questionId);
      setAiAnalystQuestionHistory((prev) => ({ ...prev, [questionId]: history }));
    } finally {
      setAiAnalystQuestionHistoryLoading(false);
    }
  };

  const handleOpenHistoryPopover = async (
    event: React.MouseEvent<HTMLElement>,
    context: 'freechat' | 'copilot' | 'analyst'
  ) => {
    setHistoryPopoverAnchor(event.currentTarget);
    setHistoryPopoverContext(context);

    if (context === 'freechat') {
      loadFreeChatHistory();
    } else if (context === 'copilot' && selectedDashboard?.PublishedAppGuid) {
      loadCopilotHistory(selectedDashboard.PublishedAppGuid);
    } else if (context === 'analyst' && aiAnalystSelectedQuestionId) {
      loadAIAnalystQuestionHistory(aiAnalystSelectedQuestionId);
    }
  };

  const handleCloseHistoryPopover = () => {
    setHistoryPopoverAnchor(null);
    setHistoryPopoverContext(null);
  };

  // Load Copilot
  const loadCopilot = async () => {
    try {
      setIsLoadingCopilot(true);
      setCopilotError(null);

      const [sessionData, resolvedClientData] = await Promise.all([
        snowflakeService.createGuardrailCopilotSession(),
        resolveClientData(),
      ]);

      const conversationId = selectedDashboard?.conversation_id;

      if (!conversationId) {
        throw new Error('No conversation ID available from selected dashboard');
      }

      if (!createdConversations.has(conversationId)) {
        try {
          await snowflakeService.createGuardrailTotalSerpConversation(conversationId);
          console.log('Copilot conversation created in Guardrail successfully');
          setCreatedConversations((prev) => new Set(prev).add(conversationId));
        } catch (conversationError) {
          console.warn('Failed to create guardrail conversation:', conversationError);
          setCreatedConversations((prev) => new Set(prev).add(conversationId));
        }
      } else {
        console.log('Guardrail conversation already tracked as created');
      }

      const url = buildGuardrailChatUrl(
        sessionData.base_url,
        conversationId,
        sessionData.token,
        sessionData.profile_id,
        resolvedClientData
      );

      setCopilotUrl(url);
      // Refresh history list for this app (non-blocking)
      if (selectedDashboard?.PublishedAppGuid) {
        loadCopilotHistory(selectedDashboard.PublishedAppGuid);
      }
    } catch (err) {
      console.error('Failed to create Copilot session:', err);
      setCopilotError('Failed to load Copilot. Please try again.');
    } finally {
      setIsLoadingCopilot(false);
    }
  };

  // Handler for refreshing Copilot with new conversation
  const handleRefreshCopilot = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!workflowId || !selectedDashboard) {
      console.error('No workflow ID or dashboard selected for refresh');
      return;
    }

    try {
      setIsLoadingCopilot(true);
      setCopilotError(null);

      // Generate a new conversation ID
      const newConversationId = crypto.randomUUID();
      console.log('Creating new Copilot conversation:', newConversationId);

      // Save the new conversation to the backend for this specific app
      await dashboardService.upsertCopilotConversation(
        parseInt(workflowId),
        selectedDashboard.PublishedAppGuid,
        newConversationId
      );

      // Create the conversation in Guardrail (using TotalSERP app)
      try {
        await snowflakeService.createGuardrailTotalSerpConversation(newConversationId);
        console.log('New Copilot conversation created in Guardrail successfully');
        setCreatedConversations((prev) => new Set(prev).add(newConversationId));
      } catch (conversationError) {
        console.warn('Failed to create Copilot conversation in Guardrail:', conversationError);
        setCreatedConversations((prev) => new Set(prev).add(newConversationId));
      }

      // Get session data and client data, then build new URL
      const [sessionData, resolvedClientData] = await Promise.all([
        snowflakeService.createGuardrailCopilotSession(),
        resolveClientData(),
      ]);
      const url = buildGuardrailChatUrl(
        sessionData.base_url,
        newConversationId,
        sessionData.token,
        sessionData.profile_id,
        resolvedClientData
      );

      // Update the URL and force iframe reload
      setCopilotUrl(url);
      setCopilotIframeKey((prevKey) => prevKey + 1);

      // Update selectedDashboard with new conversation ID so reopening copilot uses the new ID
      setSelectedDashboard((prev) => 
        prev ? { ...prev, conversation_id: newConversationId } : null
      );

      // Trigger dashboard iframe update with new conversation ID
      setUpdatedDashboardConversationId(newConversationId);

      // Refresh copilot history list (non-blocking)
      if (selectedDashboard?.PublishedAppGuid) {
        loadCopilotHistory(selectedDashboard.PublishedAppGuid);
      }

      console.log('Copilot refreshed with new conversation');
    } catch (error: any) {
      console.error('Error refreshing Copilot:', error);
      setCopilotError('Failed to refresh Copilot. Please try again.');
    } finally {
      setIsLoadingCopilot(false);
    }
  };

  // Load Free Chat
  const loadFreeChat = async () => {
    if (freeChatUrl) return; // Already loaded

    try {
      setIsLoadingFreeChat(true);
      setFreeChatError(null);

      // Use user's authguard token and profile_cortex_agent from app settings
      const [sessionData, resolvedClientData] = await Promise.all([
        snowflakeService.getGuardrailChatConfig(),
        resolveClientData(),
      ]);

      // Get the existing conversation for question -1 from this workflow
      let conversationId: string | undefined;
      
      if (workflowId) {
        try {
          const conversationsResponse = await snowflakeService.getWorkflowConversations(workflowId);
          if (conversationsResponse.success && conversationsResponse.data) {
            const questionData = conversationsResponse.data['-1'];
            if (questionData?.conversation_id && questionData.conversation_id !== 'pending' && questionData.conversation_id !== 'failed') {
              conversationId = questionData.conversation_id;
              console.log('Found existing Free Chat conversation:', conversationId);
            }
          }
        } catch (error) {
          console.warn('Failed to fetch existing conversation for Free Chat:', error);
        }
      }

      // Fall back to local questions state
      if (!conversationId) {
        const freeChatQuestion = questions.find((q) => String(q.id) === '-1');
        if (
          freeChatQuestion?.conversation_id &&
          freeChatQuestion.conversation_id !== 'pending' &&
          freeChatQuestion.conversation_id !== 'failed'
        ) {
          conversationId = freeChatQuestion.conversation_id;
          console.log('Using Free Chat conversation from local state:', conversationId);
        }
      }

      // Still not found — create a new conversation on the fly
      if (!conversationId && workflowId) {
        console.log('No existing Free Chat conversation found — creating a new one');
        const newConversationId = crypto.randomUUID();

        const saveResponse = await snowflakeService.saveWorkflowConversation(
          workflowId,
          -1,
          newConversationId
        );

        if (!saveResponse.success) {
          throw new Error(saveResponse.error || 'Failed to create Free Chat conversation');
        }

        setQuestions((prevQuestions) =>
          prevQuestions.map((q) =>
            String(q.id) === '-1'
              ? { ...q, conversation_id: newConversationId, status: 'completed' as const, error: undefined }
              : q
          )
        );

        conversationId = newConversationId;
      }

      if (!conversationId) {
        throw new Error('No conversation ID available for Free Chat. Please try again.');
      }

      // Create the conversation in Guardrail using the user's authguard token if not already created
      if (!createdConversations.has(conversationId)) {
        try {
          await snowflakeService.createGuardrailConversationWithUserToken(conversationId);
          console.log('Free Chat conversation created in Guardrail successfully');
          setCreatedConversations((prev) => new Set(prev).add(conversationId!));
        } catch (conversationError) {
          console.warn('Failed to create Free Chat conversation in Guardrail:', conversationError);
          // Still track it to avoid repeated attempts
          setCreatedConversations((prev) => new Set(prev).add(conversationId!));
        }
      }

      const url = buildGuardrailChatUrl(
        sessionData.base_url,
        conversationId,
        sessionData.token,
        sessionData.profile_id,
        resolvedClientData
      );

      setFreeChatUrl(url);
      // Refresh history list (non-blocking)
      loadFreeChatHistory();
    } catch (err) {
      console.error('Failed to create Free Chat session:', err);
      setFreeChatError('Failed to load Free Chat. Please try again.');
    } finally {
      setIsLoadingFreeChat(false);
    }
  };

  // Resizer handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleMouseMove = React.useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;

      const container = document.querySelector('[data-dashboard-container]') as HTMLElement;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const relativeX = e.clientX - containerRect.left;
      const newWidthPercent = (relativeX / containerRect.width) * 100;

      const minWidth = isMaximized ? 20 : 30;
      const maxWidth = isMaximized ? 85 : 80;
      const constrainedWidth = Math.min(Math.max(newWidthPercent, minWidth), maxWidth);

      const indicatorX = (constrainedWidth / 100) * containerRect.width;
      setDragIndicatorX(indicatorX);
    },
    [isResizing, isMaximized]
  );

  const handleMouseUp = React.useCallback(() => {
    if (isResizing && dragIndicatorX !== null) {
      const container = document.querySelector('[data-dashboard-container]') as HTMLElement;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const newWidthPercent = (dragIndicatorX / containerRect.width) * 100;
        setDashboardWidth(newWidthPercent);
      }
    }
    setIsResizing(false);
    setDragIndicatorX(null);
  }, [isResizing, dragIndicatorX]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove, true);
      document.addEventListener('mouseup', handleMouseUp, true);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.body.style.pointerEvents = 'none';
      document.documentElement.style.pointerEvents = 'auto';
    } else {
      document.removeEventListener('mousemove', handleMouseMove, true);
      document.removeEventListener('mouseup', handleMouseUp, true);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
      document.documentElement.style.pointerEvents = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove, true);
      document.removeEventListener('mouseup', handleMouseUp, true);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
      document.documentElement.style.pointerEvents = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Load questions from Guardrail
  const loadQuestionsFromGuardrail = async () => {
    try {
      setQuestionsLoading(true);
      setQuestionsError('');

      const response = await snowflakeService.getAnalyticsQuestions();

      if (response.success && response.data?.content) {
        const guardrailQuestions = response.data.content;

        if (Array.isArray(guardrailQuestions)) {
          let conversationsByQuestion: Record<string, { conversation_id: string; error_message: string | null }> = {};
          if (workflowId) {
            try {
              const conversationsResponse = await snowflakeService.getWorkflowConversations(workflowId);
              if (conversationsResponse.success && conversationsResponse.data) {
                conversationsByQuestion = conversationsResponse.data;
              }
            } catch (error) {
              console.error('Error loading conversation statuses:', error);
            }
          }

          const questionsWithStatus = guardrailQuestions.map((question) => {
            const questionId = question.id;
            const conversationData = conversationsByQuestion[questionId];

            let status: 'pending' | 'completed' | 'failed' = 'pending';
            let error: string | undefined;
            let conversationId: string | undefined;

            if (conversationData) {
              const { conversation_id, error_message } = conversationData;

              if (conversation_id === 'failed') {
                status = 'failed';
                error = error_message || 'Error processing question';
              } else if (conversation_id && conversation_id !== 'pending') {
                status = 'completed';
                conversationId = conversation_id;
                error = error_message || undefined;
              } else if (conversation_id === 'pending') {
                status = 'pending';
              }
            }

            return {
              ...question,
              status,
              conversation_id: conversationId,
              error,
            };
          });

          setQuestions(questionsWithStatus);
        } else {
          setQuestions([]);
          setQuestionsError('Invalid question format received');
        }
      } else {
        setQuestions([]);
        setQuestionsError(response.error || 'Failed to load questions');
      }
    } catch (error) {
      console.error('Error loading questions from Guardrail:', error);
      setQuestions([]);
      setQuestionsError('Failed to connect to Guardrail service');
    } finally {
      setQuestionsLoading(false);
    }
  };

  // Send questions to Guardrail chat API
  const sendQuestionsToGuardrailChat = async () => {
    const metadata: any = {
      client_id: clientId,
      client_code: clientData?.client_code,
      client_name: clientData?.name,
      client_domains: clientData?.domains,
      workflow_id: workflowId,
    };

    const chatPromises = questions.map(async (question, index) => {
      // For question -1 (Free Chat), generate a new GUID, save to backend, and mark as completed
      if (String(question.id) === '-1') {
        if (question.status === 'pending' && workflowId) {
          const newConversationId = crypto.randomUUID();
          try {
            // Save the conversation to the backend
            const saveResponse = await snowflakeService.saveWorkflowConversation(
              workflowId,
              -1,
              newConversationId
            );
            
            if (saveResponse.success) {
              setQuestions((prevQuestions) =>
                prevQuestions.map((q, qIndex) =>
                  qIndex === index
                    ? {
                        ...q,
                        conversation_id: newConversationId,
                        status: 'completed' as const,
                      }
                    : q
                )
              );
              console.log('Free Chat conversation saved successfully:', newConversationId);
            } else {
              console.error('Failed to save Free Chat conversation:', saveResponse.error);
              setQuestions((prevQuestions) =>
                prevQuestions.map((q, qIndex) =>
                  qIndex === index
                    ? { ...q, status: 'failed', error: saveResponse.error || 'Failed to save conversation' }
                    : q
                )
              );
            }
          } catch (error: any) {
            console.error('Error saving Free Chat conversation:', error);
            setQuestions((prevQuestions) =>
              prevQuestions.map((q, qIndex) =>
                qIndex === index
                  ? { ...q, status: 'failed', error: error.message || 'Network error occurred' }
                  : q
              )
            );
          }
        }
        return;
      }
      if (question.question && question.status === 'pending') {
        try {
          metadata.question_id = question.id;

          const chatResponse = await snowflakeService.sendAnalyticsChatMessage({
            message: question.question,
            metadata,
          });

          if (chatResponse.success && chatResponse.data?.conversation_id) {
            const conversationId = chatResponse.data.conversation_id;

            setQuestions((prevQuestions) =>
              prevQuestions.map((q, qIndex) =>
                qIndex === index
                  ? {
                      ...q,
                      conversation_id: conversationId,
                      status: conversationId === 'pending' ? 'pending' : 'completed',
                    }
                  : q
              )
            );
          } else {
            setQuestions((prevQuestions) =>
              prevQuestions.map((q, qIndex) =>
                qIndex === index
                  ? { ...q, status: 'failed', error: chatResponse.error || 'Failed to process question' }
                  : q
              )
            );
          }
        } catch (error: any) {
          setQuestions((prevQuestions) =>
            prevQuestions.map((q, qIndex) =>
              qIndex === index ? { ...q, status: 'failed', error: error.message || 'Network error occurred' } : q
            )
          );
        }
      }
    });

    try {
      await Promise.allSettled(chatPromises);
    } catch (error) {
      // Individual errors are already handled
    }
  };

  // Handler for refreshing a specific question
  const handleRefreshQuestion = async (questionId: string) => {
    const question = questions.find((q) => q.id === questionId);
    if (!question || !question.question) return;

    try {
      // Set question to pending state first
      setQuestions((prevQuestions) =>
        prevQuestions.map((q) =>
          q.id === questionId ? { ...q, status: 'pending' as const, conversation_id: undefined } : q
        )
      );

      const metadata: any = {
        client_id: clientId,
        client_code: clientData?.client_code,
        client_name: clientData?.name,
        client_domains: clientData?.domains,
        workflow_id: workflowId,
        question_id: questionId,
        force_run: true,
      };

      // Send the chat message to get a new conversation ID
      const chatResponse = await snowflakeService.sendAnalyticsChatMessage({
        message: question.question,
        metadata,
      });

      if (chatResponse.success && chatResponse.data?.conversation_id) {
        const newConversationId = chatResponse.data.conversation_id;
        
        // Update question with new conversation ID
        setQuestions((prevQuestions) =>
          prevQuestions.map((q) =>
            q.id === questionId
              ? {
                  ...q,
                  conversation_id: newConversationId,
                  status: newConversationId === 'pending' ? 'pending' : 'completed',
                }
              : q
          )
        );
      } else {
        setQuestions((prevQuestions) =>
          prevQuestions.map((q) =>
            q.id === questionId
              ? { ...q, status: 'failed', error: chatResponse.error || 'Failed to send refresh request' }
              : q
          )
        );
      }
    } catch (error: any) {
      console.error('Error sending refresh request:', error);
      setQuestions((prevQuestions) =>
        prevQuestions.map((q) =>
          q.id === questionId ? { ...q, status: 'failed', error: error.message || 'Failed to send refresh request' } : q
        )
      );
    }
  };

  // Handler for refreshing all questions
  const handleRefreshAllQuestions = async () => {
    const allQuestions = questions.filter((q) => q.question && q.id);

    if (allQuestions.length === 0) return;

    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => ({
        ...q,
        status: 'pending' as const,
        conversation_id: undefined,
        error: undefined,
      }))
    );

    allQuestions.forEach((question) => {
      const metadata: any = {
        client_id: clientId,
        client_code: clientData?.client_code,
        client_name: clientData?.name,
        client_domains: clientData?.domains,
        workflow_id: workflowId,
        question_id: question.id,
        force_run: true,
      };

      snowflakeService
        .sendAnalyticsChatMessage({
          message: question.question!,
          metadata,
        })
        .catch((error) => {
          console.error(`Error sending refresh request for question ${question.id}:`, error);
          setQuestions((prevQuestions) =>
            prevQuestions.map((q) =>
              q.id === question.id ? { ...q, status: 'failed', error: 'Failed to send refresh request' } : q
            )
          );
        });
    });
  };

  // Polling for completed conversations
  const pollForCompletedConversations = React.useCallback(async () => {
    if (!workflowId) return;

    try {
      const response = await snowflakeService.getWorkflowConversations(workflowId);

      if (response.success && response.data) {
        const conversationsByQuestion = response.data;

        setQuestions((prevQuestions) =>
          prevQuestions.map((question) => {
            if (question.id && conversationsByQuestion[question.id]) {
              const conversationData = conversationsByQuestion[question.id];
              const { conversation_id, error_message } = conversationData;

              if (conversation_id === 'failed') {
                return {
                  ...question,
                  conversation_id: undefined,
                  status: 'failed',
                  error: error_message || 'Error processing question',
                };
              }

              if (conversation_id && conversation_id !== 'pending') {
                return {
                  ...question,
                  conversation_id: conversation_id,
                  status: 'completed',
                  error: error_message || undefined,
                };
              }

              if (conversation_id === 'pending' && question.status !== 'pending') {
                return {
                  ...question,
                  status: 'pending',
                  conversation_id: undefined,
                  error: undefined,
                };
              }
            }
            return question;
          })
        );
      }
    } catch (error: any) {
      console.error('Error polling for completed conversations:', error);
    }
  }, [workflowId]);

  // Set up polling for pending conversations
  useEffect(() => {
    const hasPendingQuestions = questions.some((q) => q.status === 'pending');

    if (hasPendingQuestions) {
      console.log('Starting polling - pending questions detected');
      const pollInterval = setInterval(pollForCompletedConversations, 3000);

      return () => {
        console.log('Stopping polling - no pending questions');
        clearInterval(pollInterval);
      };
    } else {
      console.log('No pending questions - polling not started');
    }
  }, [questions, pollForCompletedConversations]);

  // Fetch client data
  useEffect(() => {
    const fetchClientData = async () => {
      if (numericClientId) {
        try {
          const client = await clientService.getClient(numericClientId);
          setClientData(client);
        } catch (error) {
          console.error('Failed to fetch client data:', error);
        }
      }
    };

    fetchClientData();
  }, [numericClientId]);

  // Load questions when component mounts
  useEffect(() => {
    loadQuestionsFromGuardrail();
  }, []);

  // Process questions through Guardrail chat API when questions are loaded
  useEffect(() => {
    if (questions.length > 0 && clientData) {
      sendQuestionsToGuardrailChat();
    }
  }, [questions.length, clientData]);

  // Adjust dashboard width when switching between maximized and normal modes
  useEffect(() => {
    if (isCopilotOpen) {
      const minWidth = isMaximized ? 20 : 30;
      const maxWidth = isMaximized ? 85 : 80;

      if (dashboardWidth < minWidth) {
        setDashboardWidth(minWidth);
      } else if (dashboardWidth > maxWidth) {
        setDashboardWidth(maxWidth);
      }
    }
  }, [isMaximized, isCopilotOpen, dashboardWidth]);

  // Hide body scrollbar when maximized
  useEffect(() => {
    if (isMaximized) {
      // Store original overflow value
      const originalOverflow = document.body.style.overflow;
      // Hide scrollbar
      document.body.style.overflow = 'hidden';
      
      // Cleanup: restore original overflow when unmounting or un-maximizing
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isMaximized]);

  const iframeStyle = {
    border: 'none',
    display: 'flex',
    flex: 1,
    height: '100%',
    minHeight: isMaximized ? 0 : '500px',
    margin: 0,
    padding: 0,
  };

  const tabContentStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    border: isMaximized ? 'none' : '1px solid #e0e0e0',
    borderTop: 'none',
    borderRadius: isMaximized ? 0 : 1,
    backgroundColor: isMaximized ? 'background.default' : 'transparent',
    boxShadow: isMaximized ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
    mx: 0,
    p: isMaximized ? 0 : '2px',
    minHeight: 0,
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...(isMaximized
          ? {
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1600,
              backgroundColor: 'background.default',
            }
          : {
              mt: -3,
              height: 'calc(100vh - 160px)',
              width: '100%',
              minHeight: '600px',
            }),
      }}
    >
      {/* Tabs */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
          mx: 0,
          px: isMaximized ? 2 : 0,
          backgroundColor: isMaximized ? 'background.paper' : 'transparent',
        }}
      >
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="explorer tabs">
          <Tab label="Dashboard" />
          <Tab label="Free Chat" />
          <Tab label="AI Analyst" />
        </Tabs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Copilot button - only show on Dashboard tab */}
          {activeTab === 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title={isCopilotOpen ? 'Close Data Copilot' : 'Open Data Copilot'} placement="top" arrow>
                <MuiButton
                  onClick={handleCopilotToggle}
                  startIcon={<CopilotIcon />}
                  variant="text"
                  size="small"
                  sx={{
                    color: isCopilotOpen ? 'warning.dark' : 'grey.600',
                    textTransform: 'none',
                    fontWeight: 500,
                    minWidth: 'auto',
                    px: 1,
                    py: 0.5,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                  aria-label={isCopilotOpen ? 'Close Data Copilot' : 'Open Data Copilot'}
                >
                  Data Copilot
                </MuiButton>
              </Tooltip>

              {isCopilotOpen && (
                <>
                  <Tooltip title="New Copilot Chat" placement="top" arrow>
                    <IconButton
                      size="small"
                      onClick={handleRefreshCopilot}
                      sx={{ p: 0.5, ml: 0.5, color: 'grey.600' }}
                      aria-label="New Copilot Chat"
                    >
                      <CleaningServices fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copilot History" placement="top" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => handleOpenHistoryPopover(e, 'copilot')}
                      sx={{ p: 0.5, color: historyPopoverContext === 'copilot' ? 'primary.main' : 'grey.600' }}
                      aria-label="Copilot History"
                    >
                      <HistoryIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          )}

          {/* Refresh + History buttons - only show on Free Chat tab */}
          {activeTab === 1 && (
            <>
              <Tooltip title="New Free Chat" placement="top" arrow>
                <IconButton
                  onClick={handleRefreshFreeChat}
                  sx={{ p: 0.5 }}
                  aria-label="New Free Chat"
                >
                  <CleaningServices />
                </IconButton>
              </Tooltip>
              <Tooltip title="Free Chat History" placement="top" arrow>
                <IconButton
                  onClick={(e) => handleOpenHistoryPopover(e, 'freechat')}
                  sx={{ p: 0.5, color: historyPopoverContext === 'freechat' ? 'primary.main' : 'inherit' }}
                  aria-label="Free Chat History"
                >
                  <HistoryIcon />
                </IconButton>
              </Tooltip>
            </>
          )}

          {/* Refresh + History buttons - only show on AI Analyst tab */}
          {activeTab === 2 && (
            <>
              <Tooltip 
                title={aiAnalystShowingDashboard ? "Refresh All Questions" : "Refresh Current Question"} 
                placement="top" 
                arrow
              >
                <IconButton
                  onClick={handleRefreshAIAnalyst}
                  sx={{ p: 0.5 }}
                  aria-label={aiAnalystShowingDashboard ? "Refresh All Questions" : "Refresh Current Question"}
                >
                  <CleaningServices />
                </IconButton>
              </Tooltip>
              {!aiAnalystShowingDashboard && aiAnalystSelectedQuestionId && (
                <Tooltip title="Question Chat History" placement="top" arrow>
                  <IconButton
                    onClick={(e) => handleOpenHistoryPopover(e, 'analyst')}
                    sx={{ p: 0.5, color: historyPopoverContext === 'analyst' ? 'primary.main' : 'inherit' }}
                    aria-label="Question Chat History"
                  >
                    <HistoryIcon />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}

          {/* Maximize/minimize toggle */}
          <Tooltip
            title={isMaximized ? 'Exit full screen mode' : 'Enter full screen mode'}
            placement="top"
            arrow
          >
            <IconButton
              onClick={handleMaximizeToggle}
              sx={{ p: 0.5 }}
              aria-label={isMaximized ? 'Exit full screen mode' : 'Enter full screen mode'}
            >
              {isMaximized ? <CloseFullscreen /> : <OpenInFull />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Tab Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* Dashboard Tab */}
        <Box
          data-dashboard-container
          sx={{
            ...tabContentStyle,
            display: activeTab === 0 ? 'flex' : 'none',
            flexDirection: 'row',
          }}
        >
          {/* Dashboard Content */}
          <Box
            sx={{
              width: isCopilotOpen ? (isDashboardHidden ? '0%' : `${dashboardWidth}%`) : '100%',
              transition: 'width 0.3s ease-in-out',
              display: isDashboardHidden ? 'none' : 'flex',
              flexDirection: 'column',
              minWidth: 0,
              borderRight: isCopilotOpen && !isDashboardHidden ? '1px solid #e0e0e0' : 'none',
            }}
          >
            <DashboardNavigation
              clientId={clientId}
              workflowId={workflowId}
              clientData={clientData}
              iframeStyle={iframeStyle}
              isMaximized={isMaximized}
              onSelectedDashboardChange={handleSelectedDashboardChange}
              updatedConversationId={updatedDashboardConversationId}
            />
          </Box>

          {/* Resizer Divider */}
          {isCopilotOpen && !isDashboardHidden && (
            <Box
              onMouseDown={handleMouseDown}
              sx={{
                width: '4px',
                cursor: 'col-resize',
                backgroundColor: 'divider',
                '&:hover': {
                  backgroundColor: 'primary.main',
                },
                transition: 'background-color 0.2s ease',
                flexShrink: 0,
              }}
            />
          )}

          {/* Drag Overlay */}
          {isResizing && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 999,
                cursor: 'col-resize',
                backgroundColor: 'transparent',
              }}
            />
          )}

          {/* Drag Indicator Line */}
          {isResizing && dragIndicatorX !== null && (
            <Box
              sx={{
                position: 'absolute',
                left: `${dragIndicatorX}px`,
                top: 0,
                bottom: 0,
                width: '2px',
                backgroundColor: 'primary.main',
                zIndex: 1000,
                pointerEvents: 'none',
                boxShadow: '0 0 4px rgba(25, 118, 210, 0.5)',
              }}
            />
          )}

          {/* Copilot Panel */}
          {isCopilotOpen && (
            <Box
              sx={{
                width: isDashboardHidden ? '100%' : `${100 - dashboardWidth}%`,
                transition: 'width 0.3s ease-in-out',
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                backgroundColor: 'background.paper',
              }}
            >
              {/* Copilot Header */}
              <Box
                sx={{
                  p: isMaximized ? 0.5 : 1,
                  borderBottom: '1px solid #e0e0e0',
                  backgroundColor: 'grey.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => setIsDashboardHidden(!isDashboardHidden)}
                    sx={{ p: 0.5 }}
                    title={isDashboardHidden ? 'Show Dashboard' : 'Hide Dashboard'}
                  >
                    <KeyboardArrowLeft fontSize="small" />
                  </IconButton>
                  <Typography variant={isMaximized ? 'body2' : 'subtitle2'} sx={{ fontWeight: 600 }}>
                    Copilot
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => {
                    setIsCopilotOpen(false);
                    setIsDashboardHidden(false);
                  }}
                  sx={{ p: 0.5 }}
                  title="Close Copilot"
                >
                  <KeyboardArrowRight fontSize="small" />
                </IconButton>
              </Box>

              {/* Copilot Content */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                {!copilotUrl ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: 1,
                      color: 'text.secondary',
                      p: 2,
                    }}
                  >
                    {isLoadingCopilot ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Loading Copilot...
                      </>
                    ) : copilotError ? (
                      <>
                        <Box sx={{ color: 'error.main', mb: 2 }}>{copilotError}</Box>
                        <MuiButton variant="contained" onClick={loadCopilot}>
                          Try again
                        </MuiButton>
                      </>
                    ) : (
                      'Initializing Copilot...'
                    )}
                  </Box>
                ) : (
                  <>
                    {/* Loading overlay when refreshing or toggling */}
                    {isLoadingCopilot && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          zIndex: 1000,
                        }}
                      >
                        <CircularProgress size={30} sx={{ mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          Loading...
                        </Typography>
                      </Box>
                    )}
                    <iframe
                      key={copilotIframeKey}
                      src={copilotUrl}
                      width="100%"
                      height="100%"
                      allow="clipboard-write"
                      style={{
                        border: 'none',
                        flex: 1,
                        minHeight: '400px',
                      }}
                      title="Copilot Chat"
                    />
                  </>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Free Chat Tab */}
        <Box
          sx={{
            ...tabContentStyle,
            display: activeTab === 1 ? 'flex' : 'none',
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {!freeChatUrl ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  color: 'text.secondary',
                  p: 2,
                }}
              >
                {isLoadingFreeChat ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading Free Chat...
                  </>
                ) : freeChatError ? (
                  <>
                    <Box sx={{ color: 'error.main', mb: 2 }}>{freeChatError}</Box>
                    <MuiButton variant="contained" onClick={loadFreeChat}>
                      Try again
                    </MuiButton>
                  </>
                ) : (
                  'Initializing Free Chat...'
                )}
              </Box>
            ) : (
              <iframe
                key={freeChatIframeKey}
                src={freeChatUrl}
                width="100%"
                height="100%"
                allow="clipboard-write"
                style={{
                  border: 'none',
                  flex: 1,
                  minHeight: '400px',
                }}
                title="Free Chat"
              />
            )}
          </Box>
        </Box>

        {/* AI Analyst Tab */}
        <Box
          sx={{
            ...tabContentStyle,
            display: activeTab === 2 ? 'flex' : 'none',
          }}
        >
          <AIAnalyst
            clientId={clientId}
            clientData={clientData}
            isMaximized={isMaximized}
            isActive={activeTab === 2}
            questions={questions.filter((q) => String(q.id) !== '-1')}
            questionsLoading={questionsLoading}
            questionsError={questionsError}
            workflowId={workflowId}
            onRefreshQuestion={handleRefreshQuestion}
            onRefreshAllQuestions={handleRefreshAllQuestions}
            onViewStateChange={handleAIAnalystViewStateChange}
            externalConversationOverride={aiAnalystConversationOverride}
            onConversationOverrideConsumed={() => setAiAnalystConversationOverride(null)}
          />
        </Box>
      </Box>

      {/* Unified chat history popover — shared by Free Chat, Copilot, and AI Analyst */}
      <Popover
        open={Boolean(historyPopoverAnchor)}
        anchorEl={historyPopoverAnchor}
        onClose={handleCloseHistoryPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ sx: { width: 240, height: 340, display: 'flex', flexDirection: 'column' } }}
      >
        {historyPopoverContext === 'freechat' && (
          <ConversationHistoryPanel
            items={freeChatHistory}
            activeId={freeChatUrl ? (new URL(freeChatUrl).searchParams.get('conversationId') ?? null) : null}
            onSelect={(id) => { handleSelectFreeChatHistory(id); handleCloseHistoryPopover(); }}
            onNewChat={() => { handleCloseHistoryPopover(); handleRefreshFreeChat({ preventDefault: () => {}, stopPropagation: () => {} } as React.MouseEvent); }}
            loading={freeChatHistoryLoading}
            title="Free Chat History"
            currentUserEmail={currentUserEmail}
          />
        )}
        {historyPopoverContext === 'copilot' && (
          <ConversationHistoryPanel
            items={copilotHistory}
            activeId={copilotUrl ? (new URL(copilotUrl).searchParams.get('conversationId') ?? null) : null}
            onSelect={(id) => handleSelectCopilotHistory(id)}
            onNewChat={() => { handleCloseHistoryPopover(); handleRefreshCopilot({ preventDefault: () => {}, stopPropagation: () => {} } as React.MouseEvent); }}
            loading={copilotHistoryLoading}
            title="Copilot History"
            currentUserEmail={currentUserEmail}
          />
        )}
        {historyPopoverContext === 'analyst' && aiAnalystSelectedQuestionId && (
          <ConversationHistoryPanel
            items={aiAnalystQuestionHistory[aiAnalystSelectedQuestionId] ?? []}
            activeId={null}
            onSelect={(id) => {
              if (aiAnalystSelectedQuestionId) {
                setAiAnalystConversationOverride({ questionId: aiAnalystSelectedQuestionId, conversationId: id });
              }
              handleCloseHistoryPopover();
            }}
            onNewChat={() => { handleCloseHistoryPopover(); handleRefreshQuestion(aiAnalystSelectedQuestionId); }}
            loading={aiAnalystQuestionHistoryLoading}
            title="Question History"
            currentUserEmail={currentUserEmail}
          />
        )}
      </Popover>
    </Box>
  );
};

export default ExploreStep;

